import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { FaWallet, FaEdit, FaSyncAlt, FaUser, FaDollarSign } from 'react-icons/fa'; // Import icons
import { Oval } from "react-loader-spinner"; // Import loader
import { StandardURL } from "../App";

// Helper function for currency formatting (improves UI readability)
const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    // Assuming KES for demonstration; change 'en-KE' and 'KES' if locale/currency differs
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'KES', 
        minimumFractionDigits: 2,
    }).format(numericAmount).replace('KES', 'Ksh ');
};

const UpdateUserBalance = () => {
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false); // New state for patch request loading
    const [editBalanceId, setEditBalanceId] = useState(null);
    const [balanceAmount, setBalanceAmount] = useState("");
    const [currentUserId, setCurrentUserId] = useState(null); // Renamed from userId to currentUserId for clarity

    useEffect(() => {
        fetchUserBalances();
    }, []);

    const fetchUserBalances = async () => {
        setLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/balance/viewuserbalances`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setBalances(response.data.response);
            toast.info(`Fetched ${response.data.response.length} balances.`);
        } catch (error) {
            toast.error("Failed to fetch balances");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (balance) => {
        setEditBalanceId(balance.user_id); // Store the user_id (used as ID for PATCH)
        setBalanceAmount(String(balance.balance)); // Set the current balance amount (as string for input)
        setCurrentUserId(balance.user_id); // Set the display user ID
    };

    const handleUpdate = async () => {
        if (!editBalanceId) {
            toast.error("No balance selected for update");
            return;
        }
        
        const token = localStorage.getItem("token");
        setIsSubmitting(true);

        try {
            const response = await axios.patch(
                `${StandardURL}/iNyumba/balance/updateuserbalances/${editBalanceId}`,
                {
                    balance_amount: balanceAmount,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success(response.data.response);
        } catch (error) {
            const errorMessage = error.response?.data?.response || "Failed to update balance.";
            toast.error(errorMessage);
        } finally {
            setEditBalanceId(null); // Hide the edit section
            setIsSubmitting(false);
            fetchUserBalances(); // Refresh the balance list
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <Oval
                    height={50}
                    width={50}
                    color="#f59e0b"
                    secondaryColor="#fde68a"
                    ariaLabel="loading-indicator"
                />
                <p className="ml-3 text-lg text-gray-700">Fetching user balances...</p>
            </div>
        );
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-xl p-6 lg:p-8">
                        
                        {/* Header */}
                        <h1 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FaWallet className="text-yellow-600 mr-3" /> Manage User Balances
                        </h1>

                        <div className="flex justify-end mb-4">
                             <button
                                onClick={fetchUserBalances}
                                className="flex items-center bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 text-sm"
                            >
                                <FaSyncAlt className="mr-2" /> Refresh Balances
                            </button>
                        </div>
                        
                        {/* Table of Balances */}
                        <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Current Balance</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {balances.length > 0 ? (
                                        balances.map((balance, index) => (
                                            <tr key={balance.user_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{balance.balance_id || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold flex items-center">
                                                    <FaUser className="mr-2 text-blue-500 w-4 h-4" />
                                                    {balance.user_id}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-green-600">
                                                    {formatCurrency(balance.balance)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                    <button
                                                        onClick={() => handleEdit(balance)}
                                                        className="text-yellow-600 hover:text-yellow-800 p-2 rounded-full hover:bg-yellow-50 transition flex items-center justify-center mx-auto"
                                                        title="Edit Balance"
                                                    >
                                                        <FaEdit className="w-5 h-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-6 text-gray-500">
                                                No user balances found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Inline Edit Form (Conditional Rendering) */}
                        {editBalanceId && (
                            <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg">
                                <h2 className="text-xl font-bold text-yellow-800 mb-4 flex items-center">
                                    <FaEdit className="mr-2" /> Adjust Balance for User ID: <span className="ml-2 text-yellow-900">{currentUserId}</span>
                                </h2>
                                <p className="text-sm text-yellow-700 mb-4">Warning: Manually setting a balance can override automated billing records. Use with caution.</p>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-yellow-800 mb-2 flex items-center">
                                        <FaDollarSign className="mr-2" /> New Balance Amount
                                    </label>
                                    <input
                                        type="number" // Changed to number for better mobile and validation UX
                                        value={balanceAmount}
                                        onChange={(e) => setBalanceAmount(e.target.value)}
                                        className="w-full px-4 py-2 border border-yellow-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 transition"
                                        placeholder="Enter the new total balance"
                                    />
                                </div>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={handleUpdate}
                                        disabled={isSubmitting}
                                        className={`flex-1 inline-flex items-center justify-center font-bold py-2.5 px-6 rounded-lg shadow-md transition duration-200 text-sm 
                                            ${isSubmitting
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-green-600 hover:bg-green-700 text-white"
                                            } disabled:opacity-70`}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Oval height={18} width={18} color="white" secondaryColor="#ffffff" strokeWidth={5} />
                                                <span className="ml-2">Updating...</span>
                                            </>
                                        ) : (
                                            "Confirm New Balance"
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setEditBalanceId(null)}
                                        disabled={isSubmitting}
                                        className="flex-1 bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold py-2.5 px-6 rounded-lg shadow-md transition duration-200 text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default UpdateUserBalance;